import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Play } from 'lucide-react';
import BinaryBackground from './BinaryBackground';
import { contactAPI } from '../../../services/apiClient';

const Hero = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isContactOpen, setIsContactOpen] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [contactStatus, setContactStatus] = useState(null);
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const videoRef = useRef(null);

  // Configuration for Video
  const coverImage = "/Placeholder%20image.png"; // User provided placeholder
  const videoSrc = "/Eclipseer Demo Video.mp4";

  const handlePlayVideo = () => {
    setIsPlaying(true);
    // Slight delay to ensure DOM update
    setTimeout(() => {
      if (videoRef.current) {
        videoRef.current.play();
      }
    }, 100);
  };

  const submitContact = async (e) => {
    e?.preventDefault();
    if (!contactForm.name.trim() || !contactForm.email.trim() || !contactForm.message.trim()) {
      setContactStatus({ type: 'error', text: 'Please complete all fields.' });
      return;
    }
    setIsSending(true);
    setContactStatus(null);
    try {
      const name = contactForm.name.trim();
      const email = contactForm.email.trim();
      const message = contactForm.message.trim();

      await contactAPI.sendHomeContact({ name, email, message });
      setContactStatus({ type: 'success', text: 'Message sent successfully. We will get back to you shortly.' });
      setContactForm({ name: '', email: '', message: '' });
    } catch (err) {
      const errText = err?.response?.data?.error || err?.message || 'Failed to send message. Please try again.';
      setContactStatus({ type: 'error', text: errText });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <section id="home" className="relative pt-44 pb-24 px-6 min-h-screen flex flex-col items-center bg-[#050505] overflow-hidden">

      {/* Background Gradients & Binary Canvas */}
      <BinaryBackground className="absolute inset-0 w-full h-full z-0 pointer-events-none" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-[#9747FF] opacity-5 blur-[120px] rounded-full z-0 pointer-events-none" />

      <div className="container mx-auto max-w-7xl text-center z-10 relative pointer-events-none">
        {/* Headlines */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-12 pointer-events-auto"
        >
          <h1 className="text-4xl md:text-5xl lg:text-7xl font-bold font-display leading-[1.2] text-white mb-8 tracking-tight">
            <span className="text-[#9747FF]">AI Powered</span> Surveys and Analytics: <br className="hidden md:block" />
            Launch <span className="relative inline-block px-2 mx-1 transform -rotate-1">
              <span className="absolute inset-0 bg-[#9747FF] opacity-40 -skew-x-6 rounded-md"></span>
              <span className="relative">Expert Customer Research</span>
            </span> <br className="hidden md:block" />
            in <span className="text-[#9747FF]">Seconds</span>
          </h1>
          <p className="text-gray-400 text-lg md:text-xl max-w-4xl mx-auto leading-relaxed">
            Share the feedback you need. AI writes your survey instantly.
            You create a QR or a share link in one click. Your report appears immediately,
            ready to view, export and present.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.8 }}
          className="mb-10 pointer-events-auto"
        >
          <p className="text-white font-medium text-lg">
            Customer voice becomes your strongest data signal, guided by AI, powered by your team.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35, duration: 0.7 }}
          className="flex flex-col md:flex-row items-center justify-center gap-4 mb-12 pointer-events-auto"
        >
          <button
            type="button"
            onClick={() => setIsContactOpen(true)}
            className="text-white/80 hover:text-white underline underline-offset-4 decoration-2"
          >
            Have a question? Contact us →
          </button>
        </motion.div>

        {/* Video Player */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.8 }}
          className="relative w-full max-w-6xl mx-auto aspect-video bg-[#2A2A2A] rounded-3xl mb-16 shadow-2xl flex items-center justify-center group overflow-hidden border-white/5 pointer-events-auto"
        >
          {!isPlaying ? (
            <>
              <img
                src={coverImage}
                alt="Platform Dashboard"
                className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-br from-black/40 to-transparent"></div>

              {/* Play Button */}
              <button
                onClick={handlePlayVideo}
                className="w-24 h-24 bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center border-slate-400/30 group-hover:scale-110 group-hover:bg-[#9747FF] group-hover:border-[#9747FF] transition-all duration-300 shadow-xl relative z-10 translate-y-[-7px]"
              >
                <Play fill="currentColor" className="text-white ml-2 w-10 h-10" />
              </button>



            </>
          ) : (
            <video
              ref={videoRef}
              src={videoSrc}
              className="w-full h-full object-cover"
              controls
              autoPlay
            >
              Your browser does not support the video tag.
            </video>
          )}
        </motion.div>

        {isContactOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4 pointer-events-auto">
            <div className="bg-[#0d0d0d] border border-white/10 rounded-2xl max-w-xl w-full p-6 shadow-2xl relative">
              <button
                className="absolute right-4 top-4 text-white/70 hover:text-white"
                onClick={() => {
                  setIsContactOpen(false);
                  setContactStatus(null);
                }}
                aria-label="Close contact form"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
              <h3 className="text-2xl font-display font-bold text-white mb-4">Contact Eclipseer</h3>
              <p className="text-gray-400 text-sm mb-4">
                Share a few details and we’ll get back to you shortly.
              </p>
              <form className="space-y-4" onSubmit={submitContact}>
                <div>
                  <label className="block text-sm text-white mb-2">Name</label>
                  <input
                    type="text"
                    value={contactForm.name}
                    onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                    className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white focus:outline-none focus:border-[#9747FF]"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm text-white mb-2">Email</label>
                  <input
                    type="email"
                    value={contactForm.email}
                    onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                    className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white focus:outline-none focus:border-[#9747FF]"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm text-white mb-2">Message</label>
                  <textarea
                    value={contactForm.message}
                    onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                    className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-3 text-white focus:outline-none focus:border-[#9747FF]"
                    rows={4}
                    required
                  />
                </div>
                {contactStatus && (
                  <div
                    className={`text-sm ${contactStatus.type === 'success' ? 'text-green-400' : 'text-red-400'
                      }`}
                  >
                    {contactStatus.text}
                  </div>
                )}
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setIsContactOpen(false);
                      setContactStatus(null);
                    }}
                    className="px-4 py-2 rounded-lg border border-white/15 text-white hover:bg-white/5 transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSending}
                    className="px-5 py-2 rounded-lg bg-[#9747FF] text-white font-semibold hover:bg-[#833de0] transition disabled:opacity-60"
                  >
                    {isSending ? 'Sending...' : 'Send'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Hero;
